import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Home, LogOut, MessageSquare, UserPlus, Calendar, Users, ChevronDown, BookOpen, GraduationCap } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';

// Import modular components
import KPISummaryCards from '@/components/admin/KPISummaryCards';
import TutorManagementPanel from '@/components/admin/TutorManagementPanel';
import TutorPortalPanelV5 from '@/components/admin/TutorPortalPanelV5';
import AdminPaymentsPanel from '@/components/admin/AdminPaymentsPanel';
import AdminTutorPayoutsPanel from '@/components/admin/AdminTutorPayoutsPanel';
import AdminPricingPanel from '@/components/admin/AdminPricingPanel';
import AdminBookingSeriesPanel from '@/components/admin/AdminBookingSeriesPanel';
import AdminCourseBookingsPanel from '@/components/admin/AdminCourseBookingsPanel';
import AdminQuestionBankPanel from '@/components/admin/AdminQuestionBankPanel';
import DSATContentRestoreCard from '@/components/admin/DSATContentRestoreCard';
import AdminTestBuilderPanel from '@/components/admin/AdminTestBuilderPanel';
import AdminAssignmentsPanel from '@/components/admin/AdminAssignmentsPanel';
import AdminLearningPlansV2Panel from '@/components/admin/AdminLearningPlansV2Panel';
import AdminUpcomingSessionsPanel from '@/components/admin/AdminUpcomingSessionsPanel';
import AdminLearningCentrePanel from '@/components/admin/AdminLearningCentrePanel';
import AdminLearningPlansPanel from '@/components/admin/AdminLearningPlansPanel';
import AdminGamificationPanel from '@/components/admin/AdminGamificationPanel';
import AdminWeeklyWinnersPanel from '@/components/admin/AdminWeeklyWinnersPanel';
import AdminEconomyPanel from '@/components/admin/AdminEconomyPanel';
import AdminTutorLearningUsagePanel from '@/components/admin/AdminTutorLearningUsagePanel';
import CollaborationHub from '@/components/portal/CollaborationHub';
import StudentManagementPanel from '@/components/admin/StudentManagementPanel';
import AdminCanonicalTutorsPanel from '@/components/admin/AdminCanonicalTutorsPanel';
import PaymentManagementPanel from '@/components/admin/PaymentManagementPanel';
import ResourceLeadsPanel from '@/components/admin/ResourceLeadsPanel';
import EmailLogsPanel from '@/components/admin/EmailLogsPanel';
import WhatsAppScriptsPanel from '@/components/admin/WhatsAppScriptsPanel';
import Seo from '@/components/shared/Seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const AdminDashboardV5 = () => {
  const navigate = useNavigate();
  
  const [kpis, setKpis] = useState(null);
  const [tutors, setTutors] = useState([]);
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [resourceLeads, setResourceLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('tutors');
  
  useEffect(() => {
    fetchAllData();
  }, []);
  
  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchKPIs(),
        fetchTutors(),
        fetchStudents(),
        fetchPayments(),
        fetchResourceLeads()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchKPIs = async () => {
    try {
      const response = await fetch(BACKEND_URL + '/api/v4/admin/kpis', {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch KPIs');
      const data = await response.json();
      setKpis(data.kpis);
    } catch (error) {
      console.error('KPI fetch error:', error);
    }
  };
  
  const fetchTutors = async () => {
    try {
      const response = await fetch(BACKEND_URL + '/api/v4/admin/tutors', {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch tutors');
      const data = await response.json();
      setTutors(data.tutors || []);
    } catch (error) {
      console.error('Tutors fetch error:', error);
    }
  };
  
  const fetchStudents = async () => {
    try {
      const response = await fetch(BACKEND_URL + '/api/v4/admin/students', {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch students');
      const data = await response.json();
      setStudents(data.students || []); // Fixed: V4 endpoint returns 'students', not 'enquiries'
    } catch (error) {
      console.error('Students fetch error:', error);
    }
  };
  
  const fetchPayments = async () => {
    try {
      const response = await fetch(BACKEND_URL + '/api/payments/admin/list?limit=200', {
        credentials: 'include'
      });
      if (!response.ok) {
        setPayments([]);
        return;
      }
      const data = await response.json();
      setPayments(data.payments || []);
    } catch (error) {
      setPayments([]);
    }
  };
  
  const fetchResourceLeads = async () => {
    try {
      const response = await fetch(BACKEND_URL + '/api/v4/resources/admin/leads', {
        credentials: 'include'
      });
      if (!response.ok) {
        setResourceLeads([]);
        return;
      }
      const data = await response.json();
      setResourceLeads(data.leads || []);
    } catch (error) {
      console.error('Resource leads fetch error:', error);
      setResourceLeads([]);
    }
  };
  
  const handleRefresh = async () => {
    setProcessing(true);
    await fetchAllData();
    setProcessing(false);
    toast.success('Dashboard refreshed');
  };
  
  const handleLogout = () => {
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="Admin Dashboard | Winhouse Tutors"
        description="Internal Winhouse Tutors admin dashboard."
        path="/admin/v5"
        noindex
      />
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">Winhouse Tutors Admin</h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5 truncate">Business Management Dashboard</p>
              </div>
              <Badge className="bg-purple-100 text-purple-800 shrink-0">V5 PREVIEW</Badge>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <NotificationBell accent="gold" testidPrefix="admin-" />
              <Button variant="default" size="sm" onClick={() => navigate('/admin/students')} className="bg-red-700 hover:bg-red-800 text-white shrink-0" data-testid="nav-all-students">
                <Users className="h-4 w-4 mr-2" />
                All Students
              </Button>
              <Button variant="default" size="sm" onClick={() => navigate('/admin/booking-requests')} className="bg-blue-600 hover:bg-blue-700 text-white shrink-0" data-testid="nav-booking-requests">
                <Calendar className="h-4 w-4 mr-2" />
                Bookings
              </Button>
              <QuickActionsMenu navigate={navigate} onLogout={handleLogout} onRefresh={handleRefresh} processing={processing} />
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-6 py-6">
        <Phase4CKpis />
        <KPISummaryCards kpis={kpis} loading={false} />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap gap-1 w-full h-auto max-w-7xl mb-6 justify-start">
            <TabsTrigger value="tutors">Tutors ({tutors.length})</TabsTrigger>
            <TabsTrigger value="canonical-tutors" data-testid="v5-canonical-tutors-tab">Canonical Tutors</TabsTrigger>
            <TabsTrigger value="tutor-portal" data-testid="v5-tutor-portal-tab">Tutor Portal (V5)</TabsTrigger>
            <TabsTrigger value="students">Students ({students.length})</TabsTrigger>
            <TabsTrigger value="course-bookings" data-testid="v5-course-bookings-tab">Course Bookings</TabsTrigger>
            <TabsTrigger value="question-bank" data-testid="v5-question-bank-tab">Question Bank</TabsTrigger>
            <TabsTrigger value="test-builder" data-testid="v5-test-builder-tab">Test Builder</TabsTrigger>
            <TabsTrigger value="assignments" data-testid="v5-assignments-tab">Assignments</TabsTrigger>
            <TabsTrigger value="upcoming" data-testid="v5-upcoming-tab">Upcoming Sessions</TabsTrigger>
            <TabsTrigger value="gamification" data-testid="v5-gamification-tab">Gamification</TabsTrigger>
            <TabsTrigger value="payments">Payments ({payments.length})</TabsTrigger>
            <TabsTrigger value="tutor-payouts" data-testid="v5-tutor-payouts-tab">Tutor Payouts</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="v5-pricing-tab">Pricing & FX</TabsTrigger>
            <TabsTrigger value="learning" data-testid="v5-learning-tab">Learning Centre</TabsTrigger>
            <TabsTrigger value="hub" data-testid="v5-hub-tab">Collaboration</TabsTrigger>
            <TabsTrigger value="economy" data-testid="v5-economy-tab">Economy</TabsTrigger>
            <TabsTrigger value="leads">Leads ({resourceLeads.length})</TabsTrigger>
            <TabsTrigger value="emails" data-testid="v5-email-logs-tab">Email Log</TabsTrigger>
            <TabsTrigger value="scripts" data-testid="v5-whatsapp-scripts-tab">WhatsApp</TabsTrigger>
            <TabsTrigger value="legacy" data-testid="v5-legacy-tab" className="text-gray-500">Legacy Records</TabsTrigger>
          </TabsList>
          
          <TabsContent value="tutors">
            <TutorManagementPanel tutors={tutors} onRefresh={fetchAllData} processing={processing} />
          </TabsContent>

          <TabsContent value="canonical-tutors">
            <AdminCanonicalTutorsPanel />
          </TabsContent>

          <TabsContent value="tutor-portal">
            <TutorPortalPanelV5 />
          </TabsContent>
          
          <TabsContent value="students">
            <StudentManagementPanel students={students} tutors={tutors} onRefresh={fetchAllData} processing={processing} />
          </TabsContent>

          <TabsContent value="course-bookings">
            <AdminCourseBookingsPanel />
          </TabsContent>

          <TabsContent value="question-bank">
            <div className="space-y-4">
              <DSATContentRestoreCard />
              <AdminQuestionBankPanel />
            </div>
          </TabsContent>

          <TabsContent value="test-builder">
            <AdminTestBuilderPanel />
          </TabsContent>

          <TabsContent value="assignments">
            <AdminAssignmentsPanel />
          </TabsContent>

          <TabsContent value="upcoming">
            <AdminUpcomingSessionsPanel />
          </TabsContent>

          <TabsContent value="legacy" data-testid="v5-legacy-content">
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="text-sm font-bold text-amber-900">Legacy Scheduling Records</div>
                <div className="text-xs text-amber-800 mt-1">
                  Historical records only. New schedules must be created through <strong>Course Bookings</strong>.
                  Data below is preserved and read-only in normal use.
                </div>
              </div>
              <details className="border border-gray-200 rounded-lg" open={false}>
                <summary className="cursor-pointer px-4 py-2.5 text-sm font-bold bg-gray-50" data-testid="legacy-bookings-summary">
                  Bookings &amp; Series (legacy)
                </summary>
                <div className="p-3"><AdminBookingSeriesPanel /></div>
              </details>
              <details className="border border-gray-200 rounded-lg" open={false}>
                <summary className="cursor-pointer px-4 py-2.5 text-sm font-bold bg-gray-50" data-testid="legacy-lp2-summary">
                  Learning Plans V2 (legacy)
                </summary>
                <div className="p-3"><AdminLearningPlansV2Panel /></div>
              </details>
              <details className="border border-gray-200 rounded-lg" open={false}>
                <summary className="cursor-pointer px-4 py-2.5 text-sm font-bold bg-gray-50" data-testid="legacy-plans-summary">
                  Learning Plans (legacy)
                </summary>
                <div className="p-3"><AdminLearningPlansPanel /></div>
              </details>
            </div>
          </TabsContent>
          
          <TabsContent value="payments">
            <AdminPaymentsPanel />
          </TabsContent>

          <TabsContent value="tutor-payouts">
            <AdminTutorPayoutsPanel />
          </TabsContent>

          <TabsContent value="pricing">
            <AdminPricingPanel />
          </TabsContent>

          <TabsContent value="learning">
            <div className="space-y-4">
              <AdminLearningCentrePanel />
              <AdminTutorLearningUsagePanel />
            </div>
          </TabsContent>

          <TabsContent value="hub">
            <div data-testid="v5-hub-panel">
              <CollaborationHub isAdmin={true} />
            </div>
          </TabsContent>

          <TabsContent value="economy">
            <div data-testid="v5-economy-panel">
              <AdminEconomyPanel />
            </div>
          </TabsContent>

          <TabsContent value="gamification">
            <div className="space-y-4">
              <AdminGamificationPanel />
              <AdminWeeklyWinnersPanel />
            </div>
          </TabsContent>
          
          <TabsContent value="payments-legacy" className="hidden">
            <PaymentManagementPanel payments={payments} onRefresh={fetchPayments} />
          </TabsContent>
          
          <TabsContent value="leads">
            <ResourceLeadsPanel leads={resourceLeads} onRefresh={fetchResourceLeads} />
          </TabsContent>

          <TabsContent value="emails">
            <EmailLogsPanel />
          </TabsContent>

          <TabsContent value="scripts">
            <WhatsAppScriptsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Quick Actions Menu — collapses secondary nav into a clean dropdown
const QuickActionsMenu = ({ navigate, onLogout, onRefresh, processing }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const click = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', click);
    return () => document.removeEventListener('mousedown', click);
  }, []);
  const item = (label, Icon, onClick, testid, tone = 'text-gray-800 hover:bg-gray-50') => (
    <button onClick={() => { setOpen(false); onClick(); }} className={`w-full text-left px-3 py-2 flex items-center gap-2 text-xs ${tone}`} data-testid={testid}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
  return (
    <div ref={ref} className="relative">
      <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)} className="shrink-0" data-testid="quick-actions-trigger">
        Quick Actions <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
      </Button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-50 py-1.5 overflow-hidden" data-testid="quick-actions-menu">
          {item('Access Requests', UserPlus, () => navigate('/admin/access-requests'), 'qa-access-requests')}
          {item('User Accounts', Users, () => navigate('/admin/users'), 'qa-users')}
          {item('IELTS Admin', BookOpen, () => navigate('/admin/ielts'), 'qa-ielts')}
          {item('GMAT Admin', GraduationCap, () => navigate('/admin/gmat'), 'qa-gmat')}
          {item('Staff Hub', MessageSquare, () => navigate('/staff/dashboard'), 'qa-staff-hub', 'text-red-700 hover:bg-red-50 font-semibold')}
          <div className="border-t my-1" />
          {item('Refresh data', Loader2, onRefresh, 'qa-refresh', processing ? 'text-gray-400' : 'text-gray-800 hover:bg-gray-50')}
          {item('Home', Home, () => navigate('/'), 'qa-home')}
          {item('Logout', LogOut, onLogout, 'qa-logout', 'text-red-700 hover:bg-red-50')}
        </div>
      )}
    </div>
  );
};

// Phase 4C live KPIs (additive — sits above the existing V4 KPI strip)
const Phase4CKpis = () => {
  const [k, setK] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const res = await fetch(BACKEND_URL + '/api/admin/analytics/snapshot', { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (live) setK(d);
      } catch (_e) {}
    };
    load();
    const id = setInterval(load, 60000);
    return () => { live = false; clearInterval(id); };
  }, []);
  if (!k) return null;
  const items = [
    { l: 'Today bookings', v: k.today_bookings, c: 'from-amber-50 to-amber-100 text-amber-900' },
    { l: 'Pending approvals', v: k.pending_approvals, c: 'from-red-50 to-red-100 text-red-900' },
    { l: 'Upcoming', v: k.upcoming, c: 'from-blue-50 to-blue-100 text-blue-900' },
    { l: 'Total bookings', v: k.total_bookings, c: 'from-gray-50 to-gray-100 text-gray-900' },
    { l: 'Completion %', v: k.completion_rate_pct == null ? '—' : `${k.completion_rate_pct}%`, c: 'from-emerald-50 to-emerald-100 text-emerald-900' },
    { l: 'Active tutors', v: k.active_tutors, c: 'from-purple-50 to-purple-100 text-purple-900' },
    { l: 'Active students', v: k.active_students, c: 'from-sky-50 to-sky-100 text-sky-900' },
    { l: 'Recaps generated', v: k.recaps_generated, c: 'from-yellow-50 to-yellow-100 text-yellow-900' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-4" data-testid="phase4c-kpis">
      {items.map((it) => (
        <div key={it.l} className={`bg-gradient-to-br ${it.c} rounded-xl p-3 border border-white/40`} data-testid={`kpi-${it.l.replace(/\s+/g, '-').toLowerCase()}`}>
          <div className="text-[9px] uppercase tracking-wider font-bold opacity-70">{it.l}</div>
          <div className="text-xl font-bold mt-1">{it.v}</div>
        </div>
      ))}
    </div>
  );
};

export default AdminDashboardV5;
